import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import type { AppSettings } from '../models/settings.model';

/**
 * Result of a version availability check.
 *
 * @property needsUpdate - True if a newer version is available (per server response).
 * @property isCritical - True if the update must be applied immediately (forceUpdate=true or current < minimum).
 * @property remoteVersion - The available version from the server (currentVersion).
 */
export interface VersionCheckResult {
  needsUpdate: boolean;
  isCritical: boolean;
  remoteVersion?: string;
}

/**
 * Checks app version against server policy and determines if an update is required.
 *
 * @remarks
 * Fetches the current native app version via Capacitor `App.getInfo()` (native platforms)
 * or reads `package.json` (web/PWA). Compares against server-provided version thresholds
 * to determine update urgency (force vs. soft).
 *
 * **Version comparison:** Semantic versioning (major.minor.patch) with numeric segment parsing.
 * Example: "1.0.9" < "1.0.10" is correctly evaluated (not string-sorted as "1.0.9" > "1.0.10").
 *
 * **Force update (isCritical=true):**
 * - Current version < `minimumVersion` (mobile_app_min_ver) — applies to all platforms
 *
 * **Soft update (isCritical=false):**
 * - Current version < platform-specific minimum (ios_min_ver or android_min_ver)
 * - Current version < latest available version (currentVersion)
 *
 * @injectable
 * @example
 * ```ts
 * const svc = inject(VersionCheckService);
 * const result = await svc.checkForUpdate(appSettings);
 * if (result.isCritical) {
 *   // Show blocking modal — user must update
 * } else if (result.needsUpdate) {
 *   // Show soft update prompt — user can dismiss
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  /**
   * Compares two semantic version strings numerically.
   *
   * @param currentAppVersion - Installed version (e.g. "1.0.9")
   * @param requiredVersion - Minimum required version (e.g. "1.0.10")
   * @returns true if currentAppVersion < requiredVersion
   */
  isVersionOutdated(currentAppVersion: string, requiredVersion: string): boolean {
    const current = currentAppVersion.split('.').map(Number);
    const required = requiredVersion.split('.').map(Number);
    const len = Math.max(current.length, required.length);

    for (let i = 0; i < len; i++) {
      const c = current[i] ?? 0;
      const r = required[i] ?? 0;
      if (c < r) return true;
      if (c > r) return false;
    }
    return false;
  }


  /**
   * Resolves the platform-specific soft-update version.
   * Returns the platform-specific minimum (ios_min_ver or android_min_ver) if available,
   * otherwise falls back to the generic minimumVersion.
   *
   * @remarks
   * This is used for soft update checks only. Hard/force updates use the
   * generic `minimumVersion` (mobile_app_min_ver) which applies to all platforms.
   */
  private resolvePlatformSoftUpdateVersion(settings: AppSettings): string | undefined {
    const platform = Capacitor.getPlatform();

    if (platform === 'ios' && settings.iosMinVersion) {
      return settings.iosMinVersion;
    }
    if (platform === 'android' && settings.androidMinVersion) {
      return settings.androidMinVersion;
    }
    return undefined;
  }

  /**
   * Checks app version against server policy.
   *
   * Determines if an update is needed and whether it's critical (forced) or optional (soft).
   *
   * **Force update (critical)** when:
   *   - Current app version < `minimumVersion` (mobile_app_min_ver) — applies to all platforms
   *
   * **Soft update (optional)** when:
   *   - Current version meets force minimum, but is below platform-specific soft minimum
   *   - Or newer version is available than current version
   *
   * @param appVersion - Current app version (typically from `getAppVersion()`)
   * @param settings - Server settings from `GET /settings/app`
   * @returns Result object with `{ needsUpdate, isCritical, remoteVersion }`
   */
  checkForUpdate(appVersion: string, settings: AppSettings): VersionCheckResult {
    const minimumVersion = settings.minimumVersion;  // Generic force-update threshold (mobile_app_min_ver)
    const platformSoftVersion = this.resolvePlatformSoftUpdateVersion(settings);  // Platform-specific soft minimum
    const currentVersion = settings.currentVersion;

    // Force update: current version < mandatory minimum (applies to all platforms)
    if (minimumVersion && this.isVersionOutdated(appVersion, minimumVersion)) {
      return {
        needsUpdate: true,
        isCritical: true,  // Force update — blocking, no dismiss
        remoteVersion: currentVersion,
      };
    }

    // Soft update: check platform-specific soft minimum
    if (platformSoftVersion && this.isVersionOutdated(appVersion, platformSoftVersion)) {
      return {
        needsUpdate: true,
        isCritical: false,  // Soft update — dismissable with "Not Now"
        remoteVersion: currentVersion,
      };
    }

    // Soft update: newer version available
    if (currentVersion && this.isVersionOutdated(appVersion, currentVersion)) {
      return {
        needsUpdate: true,
        isCritical: false,  // Soft update — dismissable with "Not Now"
        remoteVersion: currentVersion,
      };
    }

    return {
      needsUpdate: false,
      isCritical: false,
      remoteVersion: currentVersion,
    };
  }
}
